import type { InventoryStatus } from './types';

/**
 * Leidt de voorraad-status af uit de hoeveelheden + written_off_at.
 * Spiegelt exact de SQL-view gift_inventory_state (072a). De view is canoniek;
 * deze helper is voor optimistische UI zonder round-trip.
 */
export function deriveInventoryStatus(lot: {
  quantity_expected?: number | null;
  quantity_available?: number | null;
  quantity_reserved?: number | null;
  written_off_at?: string | null;
}): InventoryStatus {
  const expected = lot.quantity_expected ?? 0;
  const available = lot.quantity_available ?? 0;
  const reserved = lot.quantity_reserved ?? 0;

  if (lot.written_off_at) return 'afgeschreven';
  if (available > reserved) return 'beschikbaar';
  if (reserved > 0) return 'gereserveerd';
  if (expected > 0) return 'verwacht';
  return 'op';
}

/** Toegestane status-overgangen (UI-validatie). 'afgeschreven' kan vanaf elke niet-'op' staat. */
const ALLOWED_TRANSITIONS: Record<InventoryStatus, InventoryStatus[]> = {
  verwacht: ['beschikbaar', 'afgeschreven'],
  beschikbaar: ['gereserveerd', 'op', 'afgeschreven'],
  gereserveerd: ['beschikbaar', 'op', 'afgeschreven'],
  op: [],
  afgeschreven: [],
};

export function canTransition(from: InventoryStatus, to: InventoryStatus): boolean {
  if (from === to) return true;
  return ALLOWED_TRANSITIONS[from]?.includes(to) ?? false;
}

/**
 * Splitst een behoefte over beschikbare voorraad en bij te bestellen (FR-7.7).
 * `available` = vrij beschikbaar (quantity_available - quantity_reserved).
 */
export function computeStockSplit(
  available: number,
  needed: number
): { useFromStock: number; toPurchase: number } {
  const free = Math.max(0, available);
  const want = Math.max(0, needed);
  const useFromStock = Math.min(free, want);
  return { useFromStock, toPurchase: want - useFromStock };
}

export const INVENTORY_STATUS_LABEL: Record<InventoryStatus, string> = {
  verwacht: 'Verwacht',
  beschikbaar: 'Beschikbaar',
  gereserveerd: 'Gereserveerd',
  op: 'Op',
  afgeschreven: 'Afgeschreven',
};
